import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { authAPI } from '../services/api';
import { toast } from 'sonner';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    new_password: '',
    confirm_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.new_password !== form.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (form.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, form.new_password);
      setSuccess(true);
      toast.success('Mot de passe modifié avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-20">
        <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-red-500/40 p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-300 mb-2">Lien invalide</h2>
          <p className="text-gray-400 mb-6">
            Ce lien de réinitialisation est invalide ou a expiré.
          </p>
          <Link to="/login">
            <Button className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400">
              Retour à la connexion
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-20">
        <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-green-500/40 p-8 max-w-md w-full text-center">
          <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-green-300 mb-2">Mot de passe modifié !</h2>
          <p className="text-gray-400 mb-6">
            Votre mot de passe a été réinitialisé avec succès. 
            Vous pouvez maintenant vous connecter.
          </p>
          <Button 
            onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400"
          >
            Se connecter
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-black"></div>
      
      <div className="w-full max-w-md relative z-10">
        <div className="flex justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_roleplay-hub-30/artifacts/5y3hduhe_JRI%20LOGO.jpg" 
            alt="Just Rôle It" 
            className="h-24 w-24 object-contain drop-shadow-[0_0_25px_rgba(147,51,234,0.9)]"
          />
        </div>

        <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/40 p-8">
          <div className="text-center mb-6">
            <Lock className="h-12 w-12 text-purple-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-purple-300">Nouveau mot de passe</h2>
            <p className="text-gray-400 mt-2">Choisissez votre nouveau mot de passe</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password" className="text-purple-300">Nouveau mot de passe</Label>
              <Input
                id="new_password"
                type="password"
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                className="bg-black/50 border-purple-500/30 text-white"
                required
                minLength={8}
                disabled={loading}
              />
              <p className="text-xs text-gray-500">Minimum 8 caractères</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="text-purple-300">Confirmer le mot de passe</Label>
              <Input
                id="confirm_password"
                type="password"
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                className="bg-black/50 border-purple-500/30 text-white"
                required
                disabled={loading}
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold py-6 neon-button"
            >
              {loading ? 'Modification...' : 'Réinitialiser le mot de passe'}
            </Button>
          </form>
        </Card>

        <div className="text-center mt-6">
          <Link to="/login" className="text-purple-400 hover:text-purple-300 text-sm transition-colors">
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
